import { createApi } from '@reduxjs/toolkit/query/react'
import type { S3ObjectInput } from 'src/API'
import { Storage } from 'src/utils/amplify/legacy'

const storageBaseQuery =
	() =>
	async ({
		operation,
		params,
	}: {
		operation: 'get' | 'put' | 'remove' | 'list'
		params: any
	}) => {
		try {
			let responseData: any
			if (operation === 'get') {
				responseData = await Storage.get(params.key, params.options)
			} else if (operation === 'put') {
				responseData = await Storage.put(
					params.key,
					params.object,
					params.options
				)
			} else if (operation === 'remove') {
				responseData = await Storage.remove(params.key, params.options)
			} else if (operation === 'list') {
				responseData = await Storage.list(params.path, params.options)
			}
			return { data: responseData }
		} catch (error) {
			return { error }
		}
	}

export const storageApi = createApi({
	reducerPath: 'storageApi',
	baseQuery: storageBaseQuery(),
	keepUnusedDataFor: 300, // 5 minutes cache for S3 content
	tagTypes: ['CompanyTAndC', 'InvoiceDocument'],
	endpoints: (builder) => ({
		getCompanyTAndC: builder.query<string, { s3Object: S3ObjectInput }>({
			async queryFn({ s3Object }) {
				try {
					const result: any = await Storage.get(s3Object.key, {
						level: 'protected',
						identityId: s3Object.identityId,
						download: true,
						expires: 60 * 60 * 12, // 12 hours
					})
					// result.Body is a Blob
					const text = await result.Body.text()
					return { data: text }
				} catch (error) {
					return { error }
				}
			},
			providesTags: (_result, _error, { s3Object }) => [
				{ type: 'CompanyTAndC', id: s3Object.key },
			],
		}),
		getInvoiceDocumentUrl: builder.query<
			string,
			{ s3Object: S3ObjectInput }
		>({
			async queryFn({ s3Object }) {
				try {
					const signedUrl = await Storage.get(s3Object.key, {
						level: 'protected',
						identityId: s3Object.identityId,
						// Don't use download: true for PDF preview in iframe
						// expires defaults to 15 minutes which is sufficient
					})
					return { data: signedUrl as string }
				} catch (error) {
					return { error }
				}
			},
			providesTags: (_result, _error, { s3Object }) => [
				{ type: 'InvoiceDocument', id: s3Object.key },
			],
		}),
	}),
})

export const { useGetCompanyTAndCQuery, useGetInvoiceDocumentUrlQuery } =
	storageApi
